import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../context/AuthContext";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { NETWORK_ROLES } from "../constants/roles";

const ROLES = NETWORK_ROLES;

const empty = {
  name: "",
  role: "Editor",
  pronouns: "",
  location: "",
  bio: "",
  neurodivergence: "",
  skills: "",
  email: "",
  website: "",
  submitter_email: "",
};

export default function JoinNetwork() {
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        neurodivergence: form.neurodivergence.split(",").map((s) => s.trim()).filter(Boolean),
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await axios.post(`${API}/submissions/professionals`, payload);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit. Please check the fields and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-[#FFF8E7] min-h-[70vh]" data-testid="page-join-network-success">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="bg-[#1A5DFF] text-[#FFF8E7] border-2 border-[#0A0A0A] pop-shadow p-10">
            <CheckCircle2 className="w-12 h-12 mb-4" />
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FFD93D] mb-3">Submitted</div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight leading-tight">
              Welcome — your profile is queued.
            </h1>
            <p className="mt-4 text-[#FFE8A8] leading-relaxed">
              We&apos;ll review your submission and publish it onto the directory once approved. You&apos;ll hear from us at {form.submitter_email || "the email you provided"}.
            </p>
            <div className="mt-6 flex gap-3 flex-wrap">
              <button
                onClick={() => { setDone(false); setForm(empty); }}
                data-testid="join-another-btn"
                className="bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] rounded-none px-6 py-3 text-sm font-medium inline-flex items-center gap-2"
              >
                Submit another <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate("/")}
                className="bg-transparent border-2 border-[#FFD93D] text-[#FFD93D] hover:bg-[#FFD93D] hover:text-[#0A0A0A] rounded-none px-6 py-3 text-sm font-medium"
              >
                Back home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FFF8E7]" data-testid="page-join-network">
      <section className="relative overflow-hidden border-b-2 border-[#0A0A0A]">
        <div className="absolute inset-0 halftone-blue opacity-[0.08]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="inline-block bg-[#FF2E63] text-[#FFF8E7] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-4 border-2 border-[#0A0A0A]">Join the directory</div>
          <h1 className="font-heading font-black text-[#0A0A0A] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            Join the <span className="text-[#1A5DFF] [text-shadow:3px_3px_0_#0A0A0A]">network.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#2C2C2C] leading-relaxed">
            Are you a neurodivergent film professional? Add your profile to our directory. Submissions are reviewed by the team before publishing.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={submit} className="bg-white border-2 border-[#0A0A0A] pop-shadow p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="join-network-form">
            <Field label="Full name" required>
              <input data-testid="jn-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className={inputCls} />
            </Field>
            <Field label="Pronouns" hint="e.g. they/them">
              <input data-testid="jn-pronouns" value={form.pronouns} onChange={(e) => set("pronouns", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Role" required>
              <select data-testid="jn-role" value={form.role} onChange={(e) => set("role", e.target.value)} className={inputCls} required>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Location" required hint="e.g. London, UK or Remote (Berlin)">
              <input data-testid="jn-location" value={form.location} onChange={(e) => set("location", e.target.value)} required className={inputCls} />
            </Field>
            <Field label="Short bio" required full hint="2–3 sentences about your practice">
              <textarea data-testid="jn-bio" rows={4} value={form.bio} onChange={(e) => set("bio", e.target.value)} required className={inputCls} />
            </Field>
            <Field label="Neurodivergence tags" hint="Comma-separated, e.g. Autism, ADHD, Dyslexia" full>
              <input data-testid="jn-nd" value={form.neurodivergence} onChange={(e) => set("neurodivergence", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Skills" hint="Comma-separated, e.g. DaVinci Resolve, Premiere, Sound design" full>
              <input data-testid="jn-skills" value={form.skills} onChange={(e) => set("skills", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Public contact email" hint="Optional — shown on your directory card">
              <input data-testid="jn-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Website" hint="Optional">
              <input data-testid="jn-website" type="url" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" className={inputCls} />
            </Field>
            <Field label="Your email (for our records)" required full hint="We&apos;ll only use this to contact you about your submission">
              <input data-testid="jn-submitter" type="email" value={form.submitter_email} onChange={(e) => set("submitter_email", e.target.value)} required className={inputCls} />
            </Field>

            {error && (
              <div className="md:col-span-2 bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 text-sm" data-testid="join-network-error" role="alert">{error}</div>
            )}

            <div className="md:col-span-2 flex flex-col sm:flex-row gap-3 items-start">
              <button type="submit" disabled={submitting} data-testid="join-network-submit" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] disabled:opacity-60 border-2 border-[#0A0A0A] pop-shadow rounded-none px-8 py-4 text-base font-medium inline-flex items-center gap-2">
                {submitting ? "Submitting…" : "Submit profile"} <ArrowRight className="w-4 h-4" />
              </button>
              <div className="text-xs text-[#2C2C2C] sm:max-w-sm pt-2">By submitting you confirm the information is yours and accurate. The team may edit minor formatting before publishing.</div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF2E63] rounded-none";

function Field({ label, hint, children, required, full }) {
  return (
    <label className={`flex flex-col gap-1.5 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A]">
        {label} {required && <span className="text-[#FF2E63]">*</span>}
      </span>
      {children}
      {hint && <span className="text-xs text-[#2C2C2C]">{hint}</span>}
    </label>
  );
}
